/**
 * StatusField - Reusable status component for Hart entities
 * 
 * FLOW:
 * 1. Admin manages statuses via Dropdown Management panel → saves to database
 * 2. This component fetches status list from the configured endpoint
 * 3. Displays whatever statuses exist in the database (fully dynamic)
 * 4. Colors determined by STATUS_COLOR_MAP - known statuses get colors, new ones get grey
 * 
 * THE ONLY HARDCODED THING: Color mapping (STATUS_COLOR_MAP in hartUtils.js)
 * Everything else is database-driven and manageable via admin panel
 * 
 * @param {object} data - The entity data
 * @param {boolean} editable - Whether the form is in edit mode
 * @param {function} onChange - Callback when status changes
 * @param {string} endpoint - API endpoint for status options (defaults to /hart/job_statuses)
 */
import { useState, useEffect } from 'react';
import { Box, Select, MenuItem, FormControl, InputLabel, Typography } from '@mui/material';
import { getStatusColor } from '../hartUtils';

const StatusField = ({ data, editable, onChange, endpoint = '/hart/job_statuses' }) => {
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const response = await fetch(endpoint, {
          credentials: 'include',
        });
        if (response.ok) {
          const statuses = await response.json();
          setStatusOptions(statuses || []);
        }
      } catch (error) {
        console.error('Error fetching status options:', error);
        setStatusOptions(endpoint === '/hart/job_statuses'
          ? [{ name: 'Active' }, { name: 'On Hold' }, { name: 'Completed' }, { name: 'Cancelled' }]
          : [{ name: 'Active' }, { name: 'Inactive' }]
        );
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatusOptions();
  }, [endpoint]);
  
  // Initialize status to 'Active' if not set (only in editable mode on mount)
  useEffect(() => {
    if (editable && !data.status && onChange) {
      onChange({ ...data, status: 'Active' });
    }
  }, [editable]); // Only run when editable changes (on mount)
  
  // Get current status - handle both object (ForeignKey) and string formats
  const getCurrentStatus = () => {
    if (!data.status) return 'Active';
    // If it's an object (ForeignKey), return the name
    if (typeof data.status === 'object' && data.status.name) {
      return data.status.name;
    }
    // If it's a string, return it as-is
    return String(data.status);
  };
  
  const currentStatus = getCurrentStatus();

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    onChange({ ...data, status: newStatus });
  };
  
  const getStatusLabel = (status) => {
    if (!status) return 'Active';
    if (typeof status === 'object' && status.name) return status.name;
    return String(status);
  };

  if (editable) {
    // Edit mode: Dropdown selector
    return (
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel id="status-field-label">Status</InputLabel>
        <Select
          labelId="status-field-label"
          id="status-field-select"
          value={currentStatus}
          label="Status"
          onChange={handleStatusChange}
          disabled={loading}
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            }
          }}
        >
          {statusOptions.map(option => (
            <MenuItem key={option.name} value={option.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getStatusColor(option.name),
                  }}
                />
                {option.name}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // View mode: Status label with colored dot
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {getStatusLabel(currentStatus)}
      </Typography>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: getStatusColor(currentStatus),
        }}
      />
    </Box>
  );
};

export default StatusField;
